import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  ActionIcon,
  Indicator,
  Box,
  Tabs,
  Burger,
  Drawer,
  Stack,
  NavLink as MantineNavLink,
  Image,
} from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { Bell, LogOut, ChevronDown, Info, MessageSquare } from 'react-feather';
import { useAuthStore } from '../../stores/auth';
import { usePersonaStore, type SwitchablePersona } from '../../stores/persona';
import { useImpersonationStore } from '../../stores/impersonation';
import { PersonaSwitcher } from '../PersonaSwitcher';
import { PersonaRouteGuard } from '../PersonaRouteGuard';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { AboutModal } from '../common/AboutModal';
import { FeedbackModal } from '../common/FeedbackModal';
import classes from './AdminLayout.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';
import { AppVersion } from '../common/AppVersion';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

// MED-1383: the tab title names the role the viewer is acting as.
const PERSONA_DOCUMENT_TITLES: Record<SwitchablePersona, string> = {
  admin: 'Medley Admin',
  cohort_manager: 'Medley Admin',
  coach: 'Medley Coach',
  participant: 'Medley',
  partner: 'Medley Partner',
};

export function AdminLayout() {
  const { user, logout } = useAuthStore();
  const { getNavItems, setIsAdmin, setIsFullAdmin, setActivePersona, isAdmin, resetToDefault, getPersonaConfig } = usePersonaStore();
  const { isImpersonating, clearImpersonation } = useImpersonationStore();
  const activePersona = usePersonaStore(state => state.activePersona);
  useDocumentTitle(PERSONA_DOCUMENT_TITLES[activePersona] ?? 'Medley');
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const [aboutModalOpened, { open: openAboutModal, close: closeAboutModal }] = useDisclosure(false);
  const [feedbackModalOpened, { open: openFeedbackModal, close: closeFeedbackModal }] = useDisclosure(false);

  // Check if user has internal access (admin or cohort manager) and set in persona store
  // Skip when impersonating — persona is managed by the impersonation flow
  useEffect(() => {
    if (!user?.personas) return;
    if (isImpersonating) return;

    const hasInternalAccess = user.personas.some(
      p => p.persona === 'internal_admin' || p.persona === 'internal_user'
    );
    const isFullAdmin = user.personas.some(
      p => p.persona === 'internal_admin'
    );
    setIsAdmin(hasInternalAccess);
    setIsFullAdmin(isFullAdmin);

    // Set active persona based on user's actual role
    // Only auto-set if not an admin (admins can switch between personas)
    if (!hasInternalAccess) {
      // For external users, set persona based on their actual persona
      const coachPersona = user.personas.find(p => p.persona === 'coach');
      const participantPersona = user.personas.find(p => p.persona === 'participant');
      const partnerPersona = user.personas.find(p => p.persona === 'partner');

      if (coachPersona) {
        setActivePersona('coach');
      } else if (participantPersona) {
        setActivePersona('participant');
      } else if (partnerPersona) {
        setActivePersona('partner');
      }
    } else if (!isFullAdmin) {
      // Internal user but not full admin = cohort manager
      setActivePersona('cohort_manager');
    }
  }, [user, isImpersonating, setIsAdmin, setIsFullAdmin, setActivePersona]);

  const handleLogout = async () => {
    clearImpersonation(); // Clear any impersonation state
    resetToDefault(); // Reset persona to admin on logout
    await logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const userInitials = user?.initials || user?.first_name?.charAt(0) || 'U';
  const userName = user?.display_name || `${user?.first_name} ${user?.last_name}` || 'User';
  const userRole = user?.job_title || user?.primary_persona?.replace('_', ' ') || 'User';

  // Get navigation items based on active persona
  const navItems = getNavItems();

  // Handle mobile nav link click
  const handleMobileNavClick = (path: string) => {
    navigate(path);
    closeMobileNav();
  };

  // Measure the banner, because its height changes with its text and the
  // screen width. A fixed height let the page cover the nav (MDF-251).
  const { ref: bannerRef, height: bannerHeight } = useElementSize();
  const headerHeight = 60 + bannerHeight;

  return (
    <AppShell header={{ height: headerHeight }} padding={0}>
      {/* Header */}
      <AppShell.Header className={classes.header} style={{ height: headerHeight }}>
        <div ref={bannerRef}>
          <ImpersonationBanner />
        </div>
        <Group h={60} px="md" justify="space-between">
          {/* Left: Burger (mobile), Logo and Navigation */}
          <Group gap="md">
            {/* Mobile burger menu */}
            <Burger
              opened={mobileNavOpened}
              onClick={toggleMobileNav}
              hiddenFrom="lg"
              size="sm"
              color="white"
            />

            <Group gap="xs" className={classes.logo} onClick={() => navigate(getPersonaConfig().defaultRoute)}>
              <Image src={medleyLogoWhite} alt="Medley" h={24} w="auto" fit="contain" />
            </Group>

            {/* Navigation Tabs - hidden on mobile/tablet */}
            <Tabs
              variant="pills"
              classNames={{
                root: classes.tabsRoot,
                list: classes.tabsList,
                tab: classes.tab,
              }}
              value={location.pathname}
              visibleFrom="lg"
            >
              <Tabs.List>
                {navItems.map(item => {
                  // Dropdown menu for items with children
                  if (item.children) {
                    const isChildActive = item.children.some(
                      child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
                    );
                    return (
                      <Menu key={item.label} trigger="hover" openDelay={50} closeDelay={100} position="bottom-start">
                        <Menu.Target>
                          <Tabs.Tab
                            value={item.label}
                            data-active={isChildActive || undefined}
                            rightSection={<ChevronDown size={12} />}
                            style={{ cursor: 'pointer' }}
                          >
                            {item.label}
                          </Tabs.Tab>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {item.children.map(child => (
                            <Menu.Item
                              key={child.path}
                              onClick={() => navigate(child.path)}
                              data-active={location.pathname === child.path || location.pathname.startsWith(child.path + '/') || undefined}
                              className={classes.dropdownItem}
                            >
                              {child.label}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    );
                  }

                  // Regular nav link
                  return (
                    <NavLink key={item.path} to={item.path!} className={classes.navLink}>
                      {({ isActive }) => (
                        <Tabs.Tab value={item.path!} data-active={isActive || undefined}>
                          {item.label}
                        </Tabs.Tab>
                      )}
                    </NavLink>
                  );
                })}
              </Tabs.List>
            </Tabs>
          </Group>

          {/* Right: Persona Switcher, Notifications, and User Menu */}
          <Group gap="md">
            {/* Persona Switcher - only visible for admins on larger screens */}
            {isAdmin && <Box visibleFrom="lg"><PersonaSwitcher /></Box>}

            <ActionIcon
              variant="subtle"
              size="lg"
              className={classes.iconButton}
              aria-label="Notifications"
            >
              <Indicator size={8} color="red" offset={4} disabled>
                <Bell size={18} />
              </Indicator>
            </ActionIcon>

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton className={classes.userButton}>
                  <Group gap="sm">
                    <Avatar
                      size="sm"
                      radius="xl"
                      color="medleyGold"
                      src={user?.avatar_url}
                    >
                      {userInitials}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={500} c="white" lh={1.2}>
                        {userName}
                      </Text>
                      <Text size="xs" c="rgba(255,255,255,0.7)" lh={1.2}>
                        {userRole}
                      </Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  leftSection={<Info size={14} />}
                  onClick={openAboutModal}
                >
                  About
                </Menu.Item>
                <Menu.Item
                  leftSection={<MessageSquare size={14} />}
                  onClick={openFeedbackModal}
                >
                  Submit Feedback
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<LogOut size={14} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={closeMobileNav}
        size="xs"
        padding="md"
        title={
          <Image src={medleyLogoWhite} alt="Medley" h={20} w="auto" fit="contain" style={{ filter: 'invert(1) brightness(0.3)' }} />
        }
        hiddenFrom="lg"
        zIndex={1000}
      >
        <Stack gap="xs">
          {/* Persona Switcher in drawer for mobile admins */}
          {isAdmin && (
            <Box mb="md">
              <PersonaSwitcher />
            </Box>
          )}

          {navItems.map(item => {
            // Nested nav for items with children
            if (item.children) {
              const isChildActive = item.children.some(
                child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
              );
              return (
                <MantineNavLink
                  key={item.label}
                  label={item.label}
                  childrenOffset={28}
                  defaultOpened={isChildActive}
                  className={classes.mobileNavLink}
                >
                  {item.children.map(child => (
                    <MantineNavLink
                      key={child.path}
                      label={child.label}
                      active={location.pathname === child.path || location.pathname.startsWith(child.path + '/')}
                      onClick={() => handleMobileNavClick(child.path)}
                      className={classes.mobileNavLink}
                    />
                  ))}
                </MantineNavLink>
              );
            }

            // Regular nav link
            return (
              <MantineNavLink
                key={item.path}
                label={item.label}
                active={location.pathname === item.path}
                onClick={() => handleMobileNavClick(item.path!)}
                className={classes.mobileNavLink}
              />
            );
          })}
        </Stack>
      </Drawer>

      {/* Main Content */}
      <AppShell.Main className={classes.main}>
        <PersonaRouteGuard>
          <Outlet />
        </PersonaRouteGuard>
      </AppShell.Main>

      {/* MED-1360: the version line follows the page content, so it never
          covers it. AppVersion hides it where it would add a scroll. */}
      <AppVersion />

      {/* About Modal */}
      <AboutModal opened={aboutModalOpened} onClose={closeAboutModal} />
      <FeedbackModal opened={feedbackModalOpened} onClose={closeFeedbackModal} />
    </AppShell>
  );
}

export default AdminLayout;
